import React, {Fragment, SyntheticEvent, useEffect, useState} from "react";
import {Link as RouterLink, useHistory, useParams} from "react-router-dom";

// Logic.
import {useDispatch} from "react-redux";
import {setAlert} from "../../../actions/alert";
import {AlertTypes} from "../../../utils/alert.utils";

// Design.
import {GenericFormContainer, Leap4Breadcrumbs, Spinner} from "@leap4/frontend";
import {AddCircle, HomeOutlined} from "@mui/icons-material"
import {Breadcrumbs, FormControlLabel, Grid, Switch, TextField, Typography} from "@mui/material"
import {CreateServiceDto, IService, UpdateServiceDto} from "@leap4/auth";

import * as _ from 'lodash';
import breadcrumbsStyle from "../../Shared/styles/breadcrumbs.styles";
import {add, deleteInstance, getById, update} from "../../../actions/generic-crud-actions";
import DialogChangePassword from "../../Shared/Dialogs/DialogChangePassword/DialogChangePassword";
import {Mode} from "@leap4/frontend/lib/components/GenericForm/IGenericFormProps";
import TocIcon from "@mui/icons-material/Toc";
import CreateIcon from "@mui/icons-material/Create";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";


interface IFormData extends Partial<IService> {
    password2?: string;
}

const AddEditService = (): JSX.Element => {
    const history = useHistory();
    const params = useParams<{ id?: string }>();
    const dispatch = useDispatch();

    const schemaName = "service";
    const addInstanceRoute = `/${schemaName}/add`;
    const editInstanceRoute = `/${schemaName}/`;

    const [isAddPage, setIsAddPage] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const bdStyles = breadcrumbsStyle();

    const [service, setService] = useState<IFormData>({
        name: "",
        external: false,
        password: "",
        password2: ""
    });


    const handleFormChange = (e: any) => {
        let {name, value} = e.target;
        setService({...service, [name]: value});
    }

    const handleSwitchChange = (e: any) => {
        service.external = !service.external
        setService(service)
    }

    // Form submit.
    const handleSubmit = async (ev: SyntheticEvent) => {
        // For block rendering.
        ev?.preventDefault();
        if (!service) {
            return;
        }

        if (isAddPage && service.password !== service.password2) {
            dispatch(setAlert("Passwords aren't matches.", AlertTypes.FAILURE) as any);
        } else {
            const payload: any = {
                ...isAddPage ? _.omit(service, "password2") : service,
            };

            if (isAddPage && payload.name && payload.password) {
                await add<CreateServiceDto>(payload, "service", history, dispatch);
            } else if (params.id && params.id !== "add") {
                await update<UpdateServiceDto>(params.id, payload, "service", history);
            }
        }
    };

    // Init form.
    useEffect(() => {
        (async () => {
            setIsLoading(true);

            const id = params.id;

            // For editUser page.
            if (id && id !== "add") {
                setIsAddPage(false);
                const instanceData = await getById(id, "service", dispatch);
                setService({...instanceData});
            }

            setIsLoading(false);
        })()
    }, []);

    if(isLoading) return (
        <Spinner />
    )

    return (
        <>
            <Leap4Breadcrumbs breadcrumbs={[
                {
                    text: `${schemaName[0].toUpperCase() + schemaName.slice(1)}s Dashboard`,
                    icon: <TocIcon/>,
                    link: `/${schemaName}s`
            },
                {
                    text: isAddPage ? "Create" : "Update",
                    link: isAddPage ? addInstanceRoute: editInstanceRoute,
                    icon: isAddPage ? <CreateIcon/> : <AutoFixNormalIcon/>
                },
                {
                    text: service.name || "New Service",
                    link: "",
                    icon: <GpsFixedIcon/>
                }
            ]}/>
                {isLoading ? (
                    <Spinner/>
                ) : (
                    <GenericFormContainer  mode={isAddPage?Mode.Create:Mode.Edit}
                                           onDeleteFunction={()=>deleteInstance(String(params.id), schemaName, history)}
                                           onCreateOrEditFunc={(ev:any)=>handleSubmit(ev)}>
                        <Grid container alignItems={"center"} justifyContent={'center'} spacing={3} sm={10}>

                            <Grid item xs={7}>
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Service Name"
                                    name="name"
                                    autoFocus
                                    type={"text"}
                                    onChange={handleFormChange}
                                    value={service?.name}
                                />
                            </Grid>
                                <Grid item xs={4} md={5}>
                                <FormControlLabel
                                    control={<Switch defaultChecked={service?.external}/>}
                                    label="External"
                                    onChange={handleSwitchChange}
                                />
                            </Grid>
                            {!isAddPage && params.id &&
                            <Grid item justifyContent="center" style={{marginBottom: "10px"}} xs={10} md={4} >
                                <DialogChangePassword id={params.id} schemaName={"service"}/>
                            </Grid>
                            }

                            {
                                // We want to display password fields only in Add mode.
                                isAddPage &&
                                <Fragment>
                                    <Grid item sm={12} md={6}>
                                        <TextField
                                            variant="outlined"
                                            margin="normal"
                                            required
                                            autoFocus
                                            fullWidth
                                            name="password"
                                            label="Password"
                                            type="password"
                                            value={service?.password}
                                            onChange={handleFormChange}
                                            autoComplete="current-password"
                                        />
                                    </Grid>
                                    <Grid item sm={12} md={6}>
                                        <TextField
                                            variant="outlined"
                                            margin="normal"
                                            required
                                            fullWidth
                                            name="password2"
                                            label="Repeat Password"
                                            type="password"
                                            value={service?.password2}
                                            onChange={handleFormChange}
                                            autoComplete="current-password"
                                        />
                                    </Grid>
                                </Fragment>}
                        </Grid>
                    </GenericFormContainer>


                )}
        </>
    );
}


export default AddEditService;
